/**
 * useNavigationDirection — provides view-transition-aware navigation.
 *
 * Intercepts internal link clicks and wraps them in document.startViewTransition()
 * so the old page animates out and the new page animates in.
 *
 * Sets data-nav-direction on <html> so CSS ::view-transition rules can
 * choose the correct animation (slide-left, slide-right, or cross-fade).
 *
 * Direction is determined by:
 *   - data-nav-direction attribute on the clicked <a> (e.g. "lateral" for tab nav)
 *   - Browser back/forward → "back"
 *   - Everything else → "forward"
 */
import { createContext, useContext, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { flushSync } from 'react-dom';

const NavigationDirectionContext = createContext(null);

const NavigationDirectionProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location.pathname);
  const isPopRef = useRef(false);
  const historyStack = useRef([location.pathname]);
  const forwardStack = useRef([]);

  // Track navigation history for swipe gestures
  useEffect(() => {
    const path = location.pathname;
    locationRef.current = path;

    if (isPopRef.current) {
      // Browser back/forward — adjust stacks
      const stack = historyStack.current;
      if (stack.length > 1 && stack[stack.length - 2] === path) {
        forwardStack.current.push(stack.pop());
      } else {
        // Forward navigation via browser
        const fwd = forwardStack.current;
        if (fwd.length > 0 && fwd[fwd.length - 1] === path) {
          stack.push(fwd.pop());
        }
      }
      isPopRef.current = false;
    } else {
      // Programmatic or link navigation — push to stack, clear forward
      const stack = historyStack.current;
      if (stack[stack.length - 1] !== path) {
        stack.push(path);
        forwardStack.current = [];
      }
    }
  }, [location.pathname]);

  // Detect browser back/forward button via popstate
  useEffect(() => {
    const handlePopState = () => {
      isPopRef.current = true;
      document.documentElement.setAttribute('data-nav-direction', 'back');
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Intercept internal <a> clicks and wrap in startViewTransition
  useEffect(() => {
    const handler = (e) => {
      const anchor = e.target.closest('a[href]');
      if (!anchor) return;

      // Skip external links, downloads, new tabs, modifier keys
      if (anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (e.defaultPrevented) return;

      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('#')) return;

      // Skip if navigating to the current page
      if (href === locationRef.current) return;

      // No View Transitions API — let React Router handle normally
      if (!document.startViewTransition) return;

      e.preventDefault();

      const direction = anchor.dataset.navDirection || 'forward';
      document.documentElement.setAttribute('data-nav-direction', direction);

      document.startViewTransition(() => {
        flushSync(() => {
          navigate(href);
        });
      });
    };

    // Capture phase so we intercept before React Router's handler
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [navigate]);

  // Swipe gesture navigation (right = back, left = forward)
  useEffect(() => {
    let startX = 0;
    let startY = 0;
    let tracking = false;

    const onTouchStart = (e) => {
      const touch = e.touches[0];
      startX = touch.clientX;
      startY = touch.clientY;
      tracking = true;
    };

    const onTouchEnd = (e) => {
      if (!tracking) return;
      tracking = false;

      const touch = e.changedTouches[0];
      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;

      // Require minimum 80px horizontal swipe, and more horizontal than vertical
      if (Math.abs(dx) < 80 || Math.abs(dy) > Math.abs(dx)) return;

      // Ignore swipes starting from form inputs or horizontal-scrolling containers
      const target = e.target;
      if (target.closest('input, textarea, [contenteditable], .swiper, [data-no-swipe]')) return;

      if (dx > 0) {
        // Swipe right → go back
        const stack = historyStack.current;
        if (stack.length > 1) {
          const prevPath = stack[stack.length - 2];
          document.documentElement.setAttribute('data-nav-direction', 'back');
          // Use navigate(-1) but wrap the visual transition manually
          if (document.startViewTransition) {
            // Pop current from history stack, push to forward stack
            forwardStack.current.push(stack.pop());
            document.startViewTransition(() => {
              flushSync(() => {
                navigate(prevPath, { replace: true });
              });
            });
          } else {
            navigate(-1);
          }
        }
      } else {
        // Swipe left → go forward
        const fwd = forwardStack.current;
        if (fwd.length > 0) {
          const nextPath = fwd[fwd.length - 1];
          document.documentElement.setAttribute('data-nav-direction', 'forward');
          if (document.startViewTransition) {
            historyStack.current.push(fwd.pop());
            document.startViewTransition(() => {
              flushSync(() => {
                navigate(nextPath, { replace: true });
              });
            });
          } else {
            navigate(1);
          }
        }
      }
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [navigate]);

  // Transition-aware navigate for programmatic use
  const transitionNavigate = useCallback((to, options = {}) => {
    const isHistoryNav = typeof to === 'number';
    const direction = isHistoryNav && to < 0 ? 'back' : (options.state?.direction || 'forward');
    document.documentElement.setAttribute('data-nav-direction', direction);

    // For back navigation (-1), use the history stack to get a known path
    // so we can wrap it in startViewTransition (history.go() is async and can't be wrapped)
    if (isHistoryNav && to === -1) {
      const stack = historyStack.current;
      // Fall back to home if no history (e.g. user landed directly on this page)
      const prevPath = stack.length > 1 ? stack[stack.length - 2] : '/';

      if (stack.length > 1) {
        forwardStack.current.push(stack.pop());
      }

      if (document.startViewTransition) {
        document.startViewTransition(() => {
          flushSync(() => {
            navigate(prevPath, { replace: true });
          });
        });
      } else {
        navigate(prevPath, { replace: true });
      }
      return;
    }

    if (isHistoryNav) {
      navigate(to, options);
      return;
    }

    // Skip if navigating to the current page
    if (to === locationRef.current) return;

    if (document.startViewTransition) {
      document.startViewTransition(() => {
        flushSync(() => {
          navigate(to, options);
        });
      });
    } else {
      navigate(to, options);
    }
  }, [navigate]);

  return (
    <NavigationDirectionContext.Provider value={transitionNavigate}>
      {children}
    </NavigationDirectionContext.Provider>
  );
};

const useTransitionNavigate = () => useContext(NavigationDirectionContext);

export { NavigationDirectionProvider, useTransitionNavigate };
